import {
  FrequencyUnit,
  Task,
  UpcomingPeriod as UpcomingPeriodType,
  UpcomingPeriodTypes,
} from '@griply/types';
import { formatDateKey } from '@griply/utils';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar12,
  MoreHorizontal,
  Plus,
  Repeat16,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
} from 'shared/components/ui/popup-menu';

import { useData } from '../../use-data';
import { ExtraOccurrenceDatepicker } from '../upcoming-extra-occurrence-datepicker';
import { UpcomingSlot } from '../upcoming-slot';
import { WeekPicker } from '../upcoming-week-picker';
import * as Styled from './upcoming-period.style';
import { usePeriodLabel } from './use-period-label';
import { usePeriodOccurrences } from './use-period-occurrences';
import { usePeriodTarget } from './use-period-target';

enum MenuOptions {
  ExtraOccurrence = 'extra-occurrence',
  Restore = 'restore',
}

export type UpcomingPeriodProps = {
  task: Task;
  period: UpcomingPeriodType;
};

export const UpcomingPeriod: React.FC<UpcomingPeriodProps> = ({
  task,
  period,
}) => {
  const { t } = useTranslation();

  const [menuOpen, setMenuOpen] = useState<MenuOptions>();
  const openExtraOccurrenceMenu = () =>
    setMenuOpen(MenuOptions.ExtraOccurrence);
  const openRestoreMenu = () => setMenuOpen(MenuOptions.Restore);
  const closeMenu = () => setMenuOpen(undefined);

  const extraOccurrenceMenuRef = useRef<HTMLButtonElement>(null);
  const restoreMenuRef = useRef<HTMLButtonElement>(null);

  const { weekStartsOn } = useData();

  const label = usePeriodLabel({ period });

  const {
    plannedCount,
    targetCount,
    showTargetCounter,
    showRestoreAction,
    handleRestore: onRestore,
  } = usePeriodTarget({ task, period });

  const {
    onAddExtraOccurrence,
    onRemoveExtraOccurrence,
    showExtraOccurrenceButton,
    addOccurrenceLabel,
  } = usePeriodOccurrences({ task, period, targetCount, plannedCount });

  const handleRestore = () => {
    onRestore();
    closeMenu();
  };

  const daysToRender = useMemo(
    () => period.days.filter(({ entry }) => !entry?.hidden),
    [period.days],
  );

  return (
    <Styled.Container>
      <Styled.Header>
        <Styled.HeaderContent>
          <Styled.StartContainer>
            <Styled.Title>{label}</Styled.Title>
            {showTargetCounter && (
              <Styled.TargetContainer
                $isUnderTarget={plannedCount < targetCount}
              >
                <Icon icon={Calendar12} width="1.2rem" height="1.2rem" />
                <Styled.TargetText>
                  {plannedCount}/{targetCount}
                </Styled.TargetText>
              </Styled.TargetContainer>
            )}
          </Styled.StartContainer>

          <Styled.EndContainer>
            {showRestoreAction && (
              <>
                <IconButton
                  onClick={openRestoreMenu}
                  ref={restoreMenuRef}
                  icon={MoreHorizontal}
                  color={Colors.Default}
                  size={Sizes.Small}
                />
                {menuOpen === MenuOptions.Restore && (
                  <PopupMenu
                    referenceElement={restoreMenuRef}
                    onOutside={closeMenu}
                  >
                    <PopupMenuList>
                      <PopupMenuButton
                        onClick={handleRestore}
                        start={<Icon icon={Repeat16} />}
                      >
                        {t('context-menus.task.labels.repeat-until-completed')}
                      </PopupMenuButton>
                    </PopupMenuList>
                  </PopupMenu>
                )}
              </>
            )}
          </Styled.EndContainer>
        </Styled.HeaderContent>
      </Styled.Header>

      {/* days */}
      <Styled.List>
        {daysToRender.map(({ date, entry }) => (
          <Styled.ListItem key={formatDateKey(date)}>
            <UpcomingSlot
              task={task}
              date={date}
              entry={entry}
              isOverdue={period.type === UpcomingPeriodTypes.Overdue}
            />
          </Styled.ListItem>
        ))}

        {showExtraOccurrenceButton && (
          <Styled.ListItem>
            <Styled.WeekDayContainer
              onClick={openExtraOccurrenceMenu}
              ref={extraOccurrenceMenuRef}
            >
              <Styled.StartIconContainer>
                <Icon icon={Plus} />
              </Styled.StartIconContainer>
              <Styled.LabelContainer>
                {addOccurrenceLabel}
              </Styled.LabelContainer>
            </Styled.WeekDayContainer>
            {menuOpen === MenuOptions.ExtraOccurrence && (
              <>
                {period.type === FrequencyUnit.Week ? (
                  <WeekPicker
                    task={task}
                    period={period}
                    referenceElement={extraOccurrenceMenuRef}
                    onOutside={closeMenu}
                    weekStartsOn={weekStartsOn}
                    disableAutoUpdate
                  />
                ) : (
                  <ExtraOccurrenceDatepicker
                    task={task}
                    value={undefined}
                    initialMonth={period.date}
                    onAdd={onAddExtraOccurrence}
                    onRemove={onRemoveExtraOccurrence}
                    referenceElement={extraOccurrenceMenuRef}
                    onOutside={closeMenu}
                    disableAutoUpdate
                  />
                )}
              </>
            )}
          </Styled.ListItem>
        )}
      </Styled.List>
    </Styled.Container>
  );
};
